<script setup lang="ts">
import { nextTick, ref, watch } from "vue";
import type { AppliedPromoCode } from "@/types";

type PromoStatus = "idle" | "editing" | "checking" | "error" | "applied";

const props = defineProps<{
  validatePromoCode: (code: string) => Promise<AppliedPromoCode>;
}>();

const emit = defineEmits<{
  promoChange: [promo: AppliedPromoCode | null];
}>();

const isOpen = ref(false);
const promoCode = ref("");
const appliedPromoCode = ref("");
const checkingPromoCode = ref("");
const status = ref<PromoStatus>("idle");
const inputRef = ref<HTMLInputElement | null>(null);
let promoRequestId = 0;

watch(promoCode, (value) => {
  const apiPromoCode = getApiPromoCode(value);

  if (!apiPromoCode) {
    promoRequestId += 1;
    appliedPromoCode.value = "";
    checkingPromoCode.value = "";
    status.value = "idle";
    emit("promoChange", null);
    return;
  }

  if (
    status.value === "checking" &&
    apiPromoCode === checkingPromoCode.value
  ) {
    return;
  }

  if (
    status.value === "applied" &&
    apiPromoCode === appliedPromoCode.value
  ) {
    return;
  }

  if (status.value === "applied") {
    appliedPromoCode.value = "";
    emit("promoChange", null);
  }

  if (status.value === "checking") {
    checkingPromoCode.value = "";
    promoRequestId += 1;
  }

  status.value = "editing";
});

function getApiPromoCode(value: string) {
  return value.trim().toLocaleLowerCase("ru-RU");
}

function getDisplayPromoCode(value: string) {
  return value.toLocaleUpperCase("ru-RU");
}

function openPromoCode() {
  isOpen.value = true;
  nextTick(() => {
    inputRef.value?.focus();
  });
}

function closePromoCode() {
  inputRef.value?.blur();
  if (!getApiPromoCode(promoCode.value)) {
    isOpen.value = false;
    status.value = "idle";
  }
}

function handlePromoFocusOut(event: FocusEvent) {
  const currentTarget = event.currentTarget as HTMLElement;
  const nextTarget = event.relatedTarget as Node | null;

  if (nextTarget && currentTarget.contains(nextTarget)) return;

  closePromoCode();
}

function handlePromoInput(event: Event) {
  const input = event.target as HTMLInputElement;
  promoCode.value = getDisplayPromoCode(input.value);
}

async function applyPromoCode() {
  const apiPromoCode = getApiPromoCode(promoCode.value);

  if (!apiPromoCode) {
    status.value = "idle";
    emit("promoChange", null);
    return;
  }

  if (
    status.value === "applied" &&
    apiPromoCode === appliedPromoCode.value
  ) {
    return;
  }

  if (status.value === "checking") {
    return;
  }

  const requestId = ++promoRequestId;

  checkingPromoCode.value = apiPromoCode;
  promoCode.value = getDisplayPromoCode(apiPromoCode);
  status.value = "checking";
  emit("promoChange", null);

  try {
    const matchedPromo = await props.validatePromoCode(apiPromoCode);

    if (
      requestId !== promoRequestId ||
      getApiPromoCode(promoCode.value) !== apiPromoCode
    ) {
      return;
    }

    appliedPromoCode.value = apiPromoCode;
    checkingPromoCode.value = "";
    status.value = "applied";
    emit("promoChange", matchedPromo);
  } catch {
    if (
      requestId !== promoRequestId ||
      getApiPromoCode(promoCode.value) !== apiPromoCode
    ) {
      return;
    }

    appliedPromoCode.value = "";
    checkingPromoCode.value = "";
    status.value = "error";
    emit("promoChange", null);
  }
}

function clearPromoCode() {
  promoRequestId += 1;
  promoCode.value = "";
  appliedPromoCode.value = "";
  checkingPromoCode.value = "";
  status.value = "idle";
  emit("promoChange", null);
  nextTick(() => {
    inputRef.value?.focus();
  });
}

function getFieldClass() {
  if (status.value === "error") return "border-darkred/40";

  return "border-brand/40";
}

function getIconClass() {
  if (status.value === "error") return "text-darkred";
  if (status.value === "applied" || status.value === "checking") {
    return "text-brand";
  }

  return "text-ink/70";
}
</script>

<template>
  <button
    v-if="!isOpen"
    class="mt-6 flex h-[56px] w-full cursor-pointer items-center gap-3 rounded-full bg-darkwhite p-4 text-left text-lg font-medium text-ink"
    type="button"
    @click="openPromoCode"
  >
    <svg class="h-6 w-6 min-w-6 text-ink/70" aria-hidden="true">
      <use href="#ticket-widget-icon-promo" />
    </svg>
    Введите промокод
    <svg
      class="ml-auto h-6 w-6 min-w-6 rotate-180 stroke-ink/70"
      aria-hidden="true"
    >
      <use href="#ticket-widget-icon-arrow-left" />
    </svg>
  </button>

  <div
    v-else
    class="mt-6 flex h-[56px] w-full items-center gap-3 rounded-full border bg-white px-4 py-3 transition"
    :class="getFieldClass()"
    @focusout="handlePromoFocusOut"
  >
    <svg class="h-6 w-6 min-w-6" :class="getIconClass()" aria-hidden="true">
      <use href="#ticket-widget-icon-promo" />
    </svg>

    <input
      ref="inputRef"
      class="min-w-0 flex-1 bg-transparent text-lg font-medium text-ink outline-none placeholder:text-ink/60"
      :value="promoCode"
      placeholder="Введите промокод"
      type="text"
      autocomplete="off"
      @input="handlePromoInput"
      @keydown.enter.prevent="applyPromoCode"
      @keydown.esc.prevent="closePromoCode"
    />

    <button
      v-if="status === 'editing' || status === 'checking'"
      class="ml-auto flex shrink-0 cursor-pointer items-center gap-1 lg:gap-3 text-sm lg:text-lg font-medium text-brand transition hover:text-brand/80 disabled:cursor-default disabled:text-brand/70"
      type="button"
      :disabled="status === 'checking'"
      @click="applyPromoCode"
    >
      {{ status === "checking" ? "Проверяем" : "Применить" }}
      <span
        v-if="status === 'checking'"
        class="relative h-6 w-6 min-w-6"
        aria-hidden="true"
      >
        <span class="absolute inset-0 rounded-full border-2 border-brand/15" />
        <span
          class="absolute inset-0 animate-spin rounded-full border-2 border-transparent border-t-brand"
        />
      </span>
      <svg v-else class="h-6 w-6 min-w-6 rotate-180 stroke-brand" aria-hidden="true">
        <use href="#ticket-widget-icon-arrow-left" />
      </svg>
    </button>

    <div
      v-else-if="status === 'error'"
      class="ml-auto flex shrink-0 items-center gap-1 lg:gap-3 text-sm lg:text-lg font-medium text-darkred"
    >
      Не найдено
      <button
        class="grid h-6 w-6 min-w-6 cursor-pointer place-items-center text-ink/70 transition hover:text-ink"
        type="button"
        aria-label="Очистить промокод"
        @click="clearPromoCode"
      >
        <svg class="h-6 w-6 stroke-current" aria-hidden="true">
          <use href="#ticket-widget-icon-close" />
        </svg>
      </button>
    </div>

    <button
      v-else-if="status === 'applied'"
      class="ml-auto flex shrink-0 cursor-pointer items-center gap-1 lg:gap-3 text-sm lg:text-lg font-medium text-brand transition hover:text-brand/80"
      type="button"
      title="Убрать промокод"
      @click="clearPromoCode"
    >
      Применен
      <svg class="h-6 w-6 min-w-6 stroke-brand" aria-hidden="true">
        <use href="#ticket-widget-icon-check" />
      </svg>
    </button>
  </div>
</template>
